import { useState } from "react";
import { Link } from "react-router-dom";
import { copy } from "../copy";
import Icon from "../components/Icon";
import { fullPageLoad } from "../navigate";
import { CALCULATOR_PATH } from "../components/EarningsCalculator";
import { api, CREDIT_BUNDLES, type Me } from "./api";

const linkClass =
  "bf-focusable flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-ground-edge text-[11px] font-bold uppercase tracking-[0.14em] text-chrome transition-colors hover:border-gold/50 hover:text-gold";

export default function Sidebar({ me }: { me: Me }) {
  const t = copy.dashboard.sidebar;
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState("");

  const buy = async (bundle: number) => {
    setError("");
    setBusy(bundle);
    const res = await api.checkout(bundle);
    if (res.ok) {
      fullPageLoad(res.data);
      return;
    }
    setError(t.checkoutFailed);
    setBusy(null);
  };

  return (
    <aside className="flex flex-col gap-4">
      <div className="rounded-2xl border border-gold/30 bg-gold/5 p-5">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-gold/15 text-lg text-gold">
          <Icon name="homeSite" />
        </span>
        <h3 className="mt-3 font-headline text-xl uppercase leading-none text-chrome">{t.claimHeading}</h3>
        <p className="mt-2 text-xs leading-relaxed text-chrome-dim">{t.claimSub}</p>
        {/* The editor is a Go page, so this is a plain link: a full page load. */}
        <a
          href="/editor"
          className="bf-focusable mt-4 flex h-10 w-full items-center justify-center whitespace-nowrap rounded-md bg-gold text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-gold-light"
        >
          {t.claimCta}
        </a>
      </div>

      <div data-testid="calculator-card" className="rounded-2xl border border-money/30 bg-money/5 p-5">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-money/15 text-lg text-money">
          <Icon name="handCoin" />
        </span>
        <h3 className="mt-3 font-headline text-xl uppercase leading-none text-chrome">{t.calculatorHeading}</h3>
        <p className="mt-2 text-xs leading-relaxed text-chrome-dim">{t.calculatorSub}</p>
        {/* Go serves the calculator page, so this is a full page load. */}
        <a
          href={CALCULATOR_PATH}
          className="bf-focusable mt-4 flex h-10 w-full items-center justify-center whitespace-nowrap rounded-md bg-money text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep"
        >
          {t.calculatorCta}
        </a>
      </div>

      <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
        <h3 className="font-headline text-xl uppercase leading-none text-chrome">{t.credits}</h3>
        <div className="mt-4 flex items-baseline gap-2">
          <span data-testid="balance" className="bf-tabular font-headline text-4xl leading-none text-gold">
            {me.balance_display}
          </span>
          <span className="text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{t.available}</span>
        </div>
        {me.cosign_credits_cents > 0 && (
          <p className="bf-tabular mt-3 rounded-md border border-ground-edge bg-ground px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-chrome-dim">
            {t.cosign(me.cosign_credits_display)}
          </p>
        )}
        {error && (
          <p role="alert" className="mt-3 text-xs text-alert">
            {error}
          </p>
        )}
        <div className="mt-4 flex flex-col gap-2">
          {CREDIT_BUNDLES.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => buy(n)}
              disabled={busy !== null}
              className="bf-focusable flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-ground-edge bg-ground text-[11px] font-bold uppercase tracking-[0.14em] text-chrome transition-colors hover:border-money/50 hover:text-money disabled:opacity-60"
            >
              <Icon name="add" className="text-base" />
              {busy === n ? t.redirecting : t.buy(n)}
            </button>
          ))}
        </div>
        <p data-testid="credits-no-fees" className="mt-3 text-[11px] leading-relaxed text-chrome-dim">
          {t.noFees}
        </p>
      </div>

      <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{t.won}</span>
          <span className="bf-tabular font-headline text-lg text-money">{me.contests_won}</span>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-ground-edge pt-3">
          <span className="text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{t.entered}</span>
          <span className="bf-tabular font-headline text-lg text-chrome">{me.contests_entered}</span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Link to="/contests" className={linkClass}>
          <Icon name="trophy" className="text-base" />
          {t.myContests}
        </Link>
        <Link to="/ranking" className={linkClass}>
          <Icon name="barChart" className="text-base" />
          {t.myRanking}
        </Link>
      </div>
    </aside>
  );
}
